import { SYSTEM_ROLES } from "@cafe/shared";
import type { Option } from "@cafe/ui";
import i18n from "@/shared/i18n";

const POPULAR_TZ = ["Asia/Bishkek", "Asia/Almaty", "Asia/Tashkent", "Asia/Dushanbe", "Europe/Moscow", "Asia/Yekaterinburg", "Asia/Novosibirsk", "Europe/Istanbul", "Asia/Dubai", "UTC"];

/** Часовые пояса: популярные сверху, затем все из Intl. */
export const timezoneOptions = (): Option[] => {
  const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? POPULAR_TZ;
  const rest = all.filter((z) => !POPULAR_TZ.includes(z));
  return [...POPULAR_TZ, ...rest].map((z) => ({ value: z, label: z.replace(/_/g, " "), keywords: [z.split("/").pop() ?? z] }));
};

const CURRENCIES = ["KGS", "KZT", "UZS", "RUB", "TJS", "USD", "EUR"] as const;

/** Валюты с подписью на текущем языке (вызывать в рендере компонента с useTranslation). */
export const currencyOptions = (): Option[] => CURRENCIES.map((c) => ({ value: c, label: `${c} — ${i18n.t(`currencies.${c}`)}` }));

/** Формат обслуживания филиала: dine_in → «В зале». */
export const formatLabel = (f: string) => (i18n.exists(`formats.${f}`) ? i18n.t(`formats.${f}` as "formats.dine_in") : f);

export const formatDateTime = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(iso)) : "—";

/** Функция тарифа: addon.sms → «SMS-рассылки»; неизвестный код — как есть. */
export const featureLabel = (code: string) => (i18n.exists(`features.${code}`) ? i18n.t(`features.${code}` as "features.core.pos") : code);

/** Системные роли хранятся с русским названием — показываем перевод, пока компания не переименовала роль. */
export const roleLabel = (name: string) => {
  const code = SYSTEM_ROLES.find((r) => r.name === name)?.code;
  return code && i18n.exists(`systemRoles.${code}`) ? i18n.t(`systemRoles.${code}` as "systemRoles.owner") : name;
};

/** Подписи реестра прав (/permissions приходит на ru) — по коду ресурса/действия, иначе как есть. */
export const permissionResourceLabel = (resource: string, fallback: string) =>
  i18n.exists(`permResources.${resource}`) ? i18n.t(`permResources.${resource}` as "permResources.menu") : fallback;
export const permissionActionLabel = (action: string, fallback: string) =>
  i18n.exists(`permActions.${action}`) ? i18n.t(`permActions.${action}` as "permActions.view") : fallback;

/** Короткое обозначение единицы: g → «г», portion → «порция». */
export const unitLabel = (u: string) => (i18n.exists(`units.${u}`) ? i18n.t(`units.${u}` as "units.g") : u);

/** Категория расхода: rent → «Аренда». */
export const expenseCategoryLabel = (c: string) => (i18n.exists(`expenseCategories.${c}`) ? i18n.t(`expenseCategories.${c}` as "expenseCategories.rent") : c);

/** Причина списания (ТЗ 08 §98): spoilage → «Порча». */
/** Назначение склада: main → «Основной». */
export const warehouseKindLabel = (k: string) => (i18n.exists(`warehouse.kinds.${k}`) ? i18n.t(`warehouse.kinds.${k}` as "warehouse.kinds.main") : k);
export const writeoffReasonLabel = (r: string) => (i18n.exists(`writeoffReasons.${r}`) ? i18n.t(`writeoffReasons.${r}` as "writeoffReasons.spoilage") : r);

/** Тип заказа: dine_in → «В зале». */
export const orderTypeLabel = (t: string) => (i18n.exists(`orderTypes.${t}`) ? i18n.t(`orderTypes.${t}` as "orderTypes.dine_in") : t);

/** Короткая дата: 2026-10-06 → «06.10». */
export const formatDay = (d: string) => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "2-digit", timeZone: "UTC" }).format(new Date(`${d}T00:00:00Z`));
